import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { track } from "../../services/analytics";

// The dashboard's one question: what needs a person right now.
//
// Takes the needs_attention payload as-is. Each row links to the Leads-page
// filter its count was built from, so the number and the list agree. Links send
// days=all because this block ignores the date range; without it the Leads
// page would fall back to the stored range and show a different number.
//
// `total` is unique leads: a lead matching three rows is one lead here. It
// counts the "attention" rows only. Appointments are dates, not tasks, and the
// AI suggestions are advice, so both sit below and stay out of the headline.
const leadsLink = (params) =>
  `/leads?${new URLSearchParams({ ...params, days: "all" }).toString()}`;

const ATTENTION_ROWS = [
  {
    key: "waiting_reply",
    label: "Texts to answer",
    help: "A patient texted and no one has replied yet.",
    tone: "amber",
    spotlight: "waiting-replies-banner",
    clickEvent: "waiting_replies_banner_clicked",
    link: () => leadsLink({ waiting_reply: "true" }),
  },
  {
    key: "past_due",
    label: "Overdue follow-ups",
    help: "The follow-up date has passed.",
    tone: "red",
    link: () => leadsLink({ follow_up_status: "past_due" }),
  },
  {
    key: "due_today",
    label: "Follow-ups due today",
    help: "Reminders set for today.",
    tone: "neutral",
    link: () => leadsLink({ follow_up_status: "due_today" }),
  },
  {
    key: "unopened",
    label: "Unopened leads",
    help: "No one on your team has opened these yet.",
    // Amber once any has sat a day; a lead from this morning is not urgent.
    tone: (reason) => (reason.over_24h > 0 ? "amber" : "neutral"),
    detail: (reason) => (reason.over_24h > 0 ? `${reason.over_24h} older than 24 hours` : null),
    link: () => leadsLink({ untouched: "true" }),
  },
  {
    key: "hot_new",
    label: "High-priority new leads",
    help: "New leads our AI marked as most likely to book.",
    tone: "neutral",
    link: () => leadsLink({ status: "new", ai_score: "3" }),
  },
];

const SCHEDULE_ROWS = [
  {
    key: "appointments_today",
    label: "Today's appointments",
    tone: "neutral",
    link: (reason) => leadsLink({ appointment_date: reason.date }),
  },
  {
    key: "appointments_tomorrow",
    label: "Tomorrow's appointments",
    tone: "neutral",
    link: (reason) => leadsLink({ appointment_date: reason.date }),
  },
];

// The AI's own next-action suggestions. Only the four that mean "a person does
// something"; waiting on the patient and no action are not work. Each label says
// what the count is of, since "Call back 1" alone does not.
const AI_ROWS = [
  { key: "call_back", label: "Leads to call back" },
  { key: "send_booking_link", label: "Leads to send a booking link" },
  { key: "verify_insurance", label: "Leads needing an insurance check" },
  { key: "answer_question", label: "Leads with a question to answer" },
].map((row) => ({
  ...row,
  tone: "neutral",
  link: () => leadsLink({ next_action: row.key }),
}));

const TONES = {
  red: { count: "text-red-700", bar: "border-l-red-500", row: "bg-red-50/60" },
  amber: { count: "text-amber-700", bar: "border-l-amber-400", row: "bg-amber-50/60" },
  neutral: { count: "text-teal-800", bar: "border-l-border", row: "bg-white" },
};

// A row that matches one lead opens that lead; a list of one wastes a click.
const destination = (row, reason) =>
  reason.count === 1 && reason.lead_id ? `/leads/${reason.lead_id}` : row.link(reason);

const visible = (rows, source) => rows.filter((row) => (source?.[row.key]?.count || 0) > 0);

// Tiles in the same format as the dashboard's status strip. The help sentence
// moves to the tooltip so the block stays short.
const TileGrid = ({ rows, source, onRowClick }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
    {rows.map((row) => {
      const reason = source[row.key];
      const toneKey = typeof row.tone === "function" ? row.tone(reason) : row.tone;
      const tone = TONES[toneKey];
      const detail = row.detail ? row.detail(reason) : null;
      return (
        <Link
          key={row.key}
          to={destination(row, reason)}
          title={row.help}
          data-attr={`needs-attention-${row.key}`}
          data-spotlight={row.spotlight}
          onClick={row.clickEvent ? () => onRowClick(row, reason) : undefined}
          className={`rounded-lg border border-border border-l-4 ${tone.bar} ${tone.row} px-3 py-2 hover:bg-teal-50 transition-colors`}
        >
          <span className={`block text-2xl font-bold ${tone.count}`}>{reason.count}</span>
          <span className="block text-xs text-gray-700">{row.label}</span>
          {detail && <span className="block text-xs text-gray-500">{detail}</span>}
        </Link>
      );
    })}
  </div>
);

const GroupLabel = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-4 mb-2">
    {children}
  </h3>
);

const NeedsAttention = ({ data, locationIds = [] }) => {
  const reasons = data?.reasons || {};
  const total = data?.total || 0;
  const waiting = reasons.waiting_reply?.count || 0;

  // Once per appearance, not on every refetch.
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current || waiting === 0) return;
    viewed.current = true;
    track("waiting_replies_banner_viewed", { location_ids: locationIds, waiting_count: waiting });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  const attentionRows = visible(ATTENTION_ROWS, reasons);
  const scheduleRows = visible(SCHEDULE_ROWS, reasons);
  const aiRows = visible(AI_ROWS, data?.next_actions);

  return (
    <section className="bg-white p-4 rounded-xl shadow-sm" data-attr="needs-attention">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Needs attention</h2>
          <p className="text-xs text-gray-500">Open right now. The date range does not apply.</p>
        </div>
        {total > 0 && (
          <p className="text-sm text-gray-600">
            <span className="text-xl font-bold text-gray-900 mr-1">{total}</span>
            {total === 1 ? "lead needs" : "leads need"} action
          </p>
        )}
      </div>

      {attentionRows.length === 0 ? (
        <div className="text-center py-3">
          <CheckCircleIcon className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
          <p className="text-gray-700 text-sm font-medium">
            Nothing needs you right now
          </p>
          <p className="text-gray-400 text-xs mt-1">
            No texts to answer, no overdue follow-ups.
          </p>
        </div>
      ) : (
        <TileGrid
          rows={attentionRows}
          source={reasons}
          onRowClick={(row, reason) =>
            track(row.clickEvent, { location_ids: locationIds, waiting_count: reason.count })
          }
        />
      )}

      {scheduleRows.length > 0 && (
        <>
          <GroupLabel>Appointments</GroupLabel>
          <TileGrid rows={scheduleRows} source={reasons} />
        </>
      )}

      {aiRows.length > 0 && (
        <>
          <GroupLabel>Suggested by our AI</GroupLabel>
          <TileGrid rows={aiRows} source={data.next_actions} />
        </>
      )}
    </section>
  );
};

export default NeedsAttention;
